import { useNavigate, useLocation } from "react-router-dom";
import AuthService from "./AuthService";
import { useBitsUserContext } from "../../context/SignedInContext";
import { useEffect, useState } from "react";
import { TripleSpinner } from "../ui/TripleSpinner";
import { isCountryUk } from "utils/country";

export function RequireAuth({ children }: { children: JSX.Element }) {
  const isUK = isCountryUk();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const { wallet, loadWallet, loadUserInfo, loadPocket, loadKyc } = useBitsUserContext();

  /* Whether the wallet request has come back at all — as opposed to having
     come back empty. Without it the guard below cannot tell "still loading"
     from "loaded and failed", and a failed load left the page on a spinner
     that never stopped. `loadWallet` catches its own errors, so this settles
     either way. */
  const [hasWalletSettled, setHasWalletSettled] = useState<boolean>(false);

  useEffect(() => {
    loadWallet().finally(() => setHasWalletSettled(true));
    loadUserInfo();
    loadPocket();
    loadKyc();
  }, []);

  const isAuthOptional = pathname.toLocaleLowerCase().startsWith("/store");

  useEffect(() => {
    if (!isAuthOptional) {
      if (!AuthService.isAuthenticated()) {
        navigate("/login", { state: { from: location } });
      }
    }
  }, [wallet]);

  /* Once the request has settled, render the page even without a wallet:
     the failure is already in `errorMessage`, and `Layout` renders it. A
     spinner that never stops tells the user nothing.

     US only. The UK app shares this guard and is live, and its signed-in
     pages have not been audited for a null wallet — so over there a failed
     load keeps the old behaviour rather than risking a crash in place of a
     spinner. Worth revisiting for the UK as its own change. */
  const isWalletBlocking = wallet == null && (isUK || !hasWalletSettled);

  if (!isAuthOptional && isWalletBlocking) {
    return (
      <>
        {isUK ? (
          <div className="vh-100 d-flex flex-column align-items-center justify-content-center">
            <TripleSpinner />
          </div>
        ) : (
          <TripleSpinner />
        )}
      </>
    );
  }

  return children;
}
